import { useCallback } from 'react';

import { ShipmentVolume } from '../../../../../../constants/features';
import { useIsSellerOnFeatureGatedPlanProfile } from '../../../../../../feature-gates/use-is-seller-on-feature-gated-plan-profile';
import { UNLIMITED_SHIPMENT_LIMIT } from '../../../constants/shipment-limits';
import { useCurrentSubscriptionShipmentLimit, useCurrentSubscriptionShipmentLimitTiers } from '../../../hooks/use-plan-details';
import { ActivationTiming } from '../constants';

/**
 * @typedef {import('../../../../../../prop-types/subscription-data-types.d.ts').BillingPlan} BillingPlan
 */

/**
 * @typedef {import('../../../types/add-on-types.d.ts').ActivationTiming} ActivationTimingType
 */

/**
 * @typedef {import('../../../types/add-on-types.d.ts').AddOnPayload} AddOnPayload
 */

/**
 *
 * @param {number} desiredShipmentLimit
 * @param {number} currentShipmentLimit
 * @returns {ActivationTimingType}
 */
const getActivationTiming = (desiredShipmentLimit, currentShipmentLimit) => {
  if (desiredShipmentLimit === UNLIMITED_SHIPMENT_LIMIT) {
    return ActivationTiming.IMMEDIATELY;
  }

  if (currentShipmentLimit === UNLIMITED_SHIPMENT_LIMIT) {
    return ActivationTiming.END_OF_BILLING_CYCLE;
  }

  return desiredShipmentLimit >= currentShipmentLimit ? ActivationTiming.IMMEDIATELY : ActivationTiming.END_OF_BILLING_CYCLE;
};

/**
 * @param {object} props
 * @param {number} props.desiredShipmentLimit
 * @param {BillingPlan} props.selectedPlan
 * @param {boolean} [props.isMigratingToNewestPlans]
 * @returns {() => { addOnPayloadToAdd: AddOnPayload, addOnPayloadToRemove: AddOnPayload }}
 */
export const useGetShipmentVolumeAddOnPayload = ({ desiredShipmentLimit, selectedPlan, isMigratingToNewestPlans = false }) => {
  const isNewLineupEnabledForSeller = useIsSellerOnFeatureGatedPlanProfile();
  const currentSubscriptionShipmentLimit = useCurrentSubscriptionShipmentLimit();
  const currentSubscriptionShipmentLimitTiers = useCurrentSubscriptionShipmentLimitTiers();

  const selectedPlanHasShipmentLimitTiers = selectedPlan?.shipmentLimit?.tiers?.length > 0;
  const currentPlanHasShipmentLimitTiers = currentSubscriptionShipmentLimitTiers?.length > 0;

  return useCallback(() => {
    const payload = {
      addOnPayloadToAdd: undefined,
      addOnPayloadToRemove: undefined,
    };

    // Skip if not on feature-gated profile AND not migrating to one
    if (!isNewLineupEnabledForSeller && !isMigratingToNewestPlans) {
      return payload;
    }

    const fromTieredPlanToTieredPlan = currentPlanHasShipmentLimitTiers && selectedPlanHasShipmentLimitTiers;
    const fromTieredPlanToNonTieredPlan = currentPlanHasShipmentLimitTiers && !selectedPlanHasShipmentLimitTiers;
    const fromNonTieredPlanToTieredPlan = !currentPlanHasShipmentLimitTiers && selectedPlanHasShipmentLimitTiers;

    if (fromTieredPlanToTieredPlan || fromNonTieredPlanToTieredPlan) {
      // Always send shipment volume when target plan has tiers, even if unchanged
      // Use current limit as fallback, with end-of-cycle timing for same/downgrade
      // TODO remove this patch once the backend correctly preserves existing shipment volume quantity on downgrade when not explicitly sent
      const firstAvailableTier = selectedPlan?.shipmentLimit?.tiers?.[0]?.endingQuantity;
      const effectiveShipmentLimit = desiredShipmentLimit || currentSubscriptionShipmentLimit || firstAvailableTier;
      payload.addOnPayloadToAdd = {
        code: ShipmentVolume,
        quantity: effectiveShipmentLimit,
        activationTiming: getActivationTiming(effectiveShipmentLimit, currentSubscriptionShipmentLimit),
      };
    } else if (fromTieredPlanToNonTieredPlan) {
      payload.addOnPayloadToRemove = {
        code: ShipmentVolume,
        quantity: currentSubscriptionShipmentLimit,
        activationTiming: getActivationTiming(desiredShipmentLimit, currentSubscriptionShipmentLimit),
      };
    }
    // from non-tiered plan to non-tiered plan, we don't need to do anything

    return payload;
  }, [
    currentPlanHasShipmentLimitTiers,
    currentSubscriptionShipmentLimit,
    desiredShipmentLimit,
    isMigratingToNewestPlans,
    isNewLineupEnabledForSeller,
    selectedPlan?.shipmentLimit?.tiers,
    selectedPlanHasShipmentLimitTiers,
  ]);
};
